import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { PersonnelOnboardingRef, PersonnelSummary } from "services/personnel.service";
import { sendLoginLink } from "services/onboarding.service";
import { fetchJobLeavesByOnboarding } from "services/job-leave.service";
import ContractAmendmentsPanel from "features/contract-amendments/ContractAmendmentsPanel";
import SensitiveAmount from "components/sensitive-amount/SensitiveAmount";
import { getOnboardingStatusLabel } from "utils/onboardingStatus";
import { Fact, PlannedSalaryChangeFact, panelClass } from "./PersonnelCardParts";
import {
  contractTypeKey,
  defaultContractRecordId,
  formatDay,
  primaryOnboarding,
  recordHasAmendments,
  recordLeaveLocks,
  termsPrefillRecord,
  termsShownForRecord,
} from "./personnel-helpers";

interface Props {
  companyId: string;
  userId: string;
  summary: PersonnelSummary;
  /** Reload the card's summary (terms, amendment list) after a change. */
  onChanged: () => void;
}

/**
 * Whether a job leave locks this record's amendments — the record view's
 * rule (only a completed hire, any leave not cancelled). `null` while
 * loading; a failed load leaves it unlocked, like the record view.
 */
function useRecordLeaveLock(companyId: string, record: PersonnelOnboardingRef | null, enabled: boolean) {
  const [locked, setLocked] = useState<boolean | null>(null);
  const recordId = record?.id;
  const completed = record?.status === "completed";

  useEffect(() => {
    if (!recordId || !enabled || !completed) {
      setLocked(false);
      return undefined;
    }
    let cancelled = false;
    setLocked(null);
    fetchJobLeavesByOnboarding(companyId, recordId)
      .then((rows) => {
        if (!cancelled) setLocked(recordLeaveLocks(rows));
      })
      .catch(() => {
        if (!cancelled) setLocked(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, recordId, enabled, completed]);

  return locked;
}

function recordLabel(record: PersonnelOnboardingRef, t: (key: string) => string, lang: "lt" | "en") {
  return [
    record.mode === "contract_registration" ? t("contract_registration") : t("personnel_record_hire"),
    formatDay(record.hiringDate),
    record.status ? getOnboardingStatusLabel(record.status as any, t as any, lang) : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * «Sutartis ir pakeitimai»: the current employment terms and the contract
 * amendments of one onboarding record (the newest not cancelled by default,
 * a selector when there are several) — the same panel and rules as the
 * onboarding record view.
 */
export default function PersonnelContractTab({ companyId, userId, summary, onChanged }: Props) {
  const { t, i18n } = useTranslation();
  const lang = ((i18n as any).language || "lt") as "lt" | "en";
  const { onboardings, employmentTerms: terms, viewerCan } = summary;
  const [selectedId, setSelectedId] = useState<string | null>(() => defaultContractRecordId(summary));
  const record = onboardings.find((item) => item.id === selectedId) || primaryOnboarding(onboardings);
  const canManage = viewerCan.manageOnboarding;
  const leaveLocked = useRecordLeaveLock(companyId, record, canManage);
  const hasSalary = !!terms && Object.prototype.hasOwnProperty.call(terms, "salary");
  // The summary carries one set of terms — the record they come from.
  const showTerms = termsShownForRecord(summary, record?.id);
  const prefillTerms = termsPrefillRecord(terms, record?.id);

  const handleSendLoginLink = async () => {
    if (!record) return;
    try {
      await sendLoginLink(companyId, record.id);
      toast.success(t("login_link_sent"));
    } catch {
      toast.error(t("error"));
    }
  };

  if (!record) return null;

  return (
    <div className="space-y-4">
      <section className={panelClass}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {onboardings.length > 1 ? (
            <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
              {t("personnel_contract_record")}
              <select
                className="rounded-md border border-gray-300 px-2 py-1.5 text-base md:text-sm"
                value={record.id}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                {onboardings.map((item) => (
                  <option key={item.id} value={item.id}>
                    {recordLabel(item, t, lang)}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span className="text-sm text-gray-700">{recordLabel(record, t, lang)}</span>
          )}
          <Link
            to={`/app/${companyId}/onboarding/${record.id}`}
            className="text-sm font-medium text-blue-700 hover:underline max-md:py-2"
          >
            {t("personnel_open_record")}
          </Link>
        </div>
      </section>

      <section className={panelClass}>
        <h2 className="mb-2 text-sm font-semibold text-gray-900">{t("personnel_tile_terms")}</h2>
        {showTerms ? (
          <div className="grid gap-x-8 gap-y-1 text-sm text-gray-700 md:grid-cols-2">
            <Fact label={t("original_contract_number")} value={terms?.originalContractNumber || ""} />
            <Fact
              label={t("contract_type")}
              value={contractTypeKey(terms?.contractType) ? t(contractTypeKey(terms?.contractType)!) : ""}
            />
            {terms?.contractVariant && (
              <Fact
                label={t("contract_variant")}
                value={t(`contract_variant_${terms.contractVariant}`, { defaultValue: terms.contractVariant })}
              />
            )}
            <Fact label={t("hiring_date")} value={formatDay(terms?.hiringDate ?? null)} />
            {terms?.contractEndDate && <Fact label={t("contract_end_date")} value={formatDay(terms.contractEndDate)} />}
            {terms?.probationMonths != null && terms.probationMonths > 0 && (
              <Fact label={t("probation_months")} value={terms.probationMonths} />
            )}
            <Fact label={t("hours_per_week")} value={terms?.hoursPerWeek ?? ""} />
            <Fact label={t("positions_title")} value={summary.positionsText} />
            {hasSalary && <Fact label={t("personnel_salary")} value={<SensitiveAmount value={terms?.salary} />} />}
            {terms?.plannedSalaryChange && <PlannedSalaryChangeFact change={terms.plannedSalaryChange} />}
          </div>
        ) : (
          <p className="text-sm text-gray-500">{t("personnel_terms_other_record")}</p>
        )}
      </section>

      <section className={panelClass}>
        {!recordHasAmendments(record) ? (
          <p className="text-sm text-gray-500">{t("personnel_amendments_after_contract")}</p>
        ) : leaveLocked === null ? (
          <p className="text-sm text-gray-500">{t("loading")}</p>
        ) : (
          <ContractAmendmentsPanel
            key={record.id}
            companyId={companyId}
            onboardingId={record.id}
            readOnly={leaveLocked || !canManage}
            leaveLocked={leaveLocked}
            canManageOnboarding={canManage && !leaveLocked}
            lang={lang}
            onSendLoginLink={handleSendLoginLink}
            employeeUserId={userId}
            employeeName={summary.person.name}
            onReloadOnboarding={onChanged}
            onChanged={onChanged}
            // Prefill salary / hours only on the record the terms come from.
            currentTerms={
              prefillTerms && terms
                ? {
                    salary: terms.salary ?? "",
                    hoursPerWeek: terms.hoursPerWeek ?? "",
                    plannedSalaryChange: terms.plannedSalaryChange ?? null,
                  }
                : undefined
            }
          />
        )}
      </section>
    </div>
  );
}
